/**
 *  Copyright AlpineReplay Inc, 2022. All rights reserved.
 *  Authors: Brian Bal
 */

import React, { useEffect, useState, useMemo } from "react"
import { useSelector } from "react-redux"
import {
    isSmallScreen,
    getProperty,
    VideoControlButton,
    Icon,
    Row,
    Element,
    generateHumanTags,
    Time,
    TimeRange,
    Color,
    isSmallMobileScreen,
    stopBenchmark,
    MulticamOnboardingDialog,
    FluidPlayerOnboardingDialog,
} from "trux"
import { VideoContainer, useFluidPlayer } from "trux/dist/organisms/VideoPlayer"
import { useVideoSettings } from "trux/dist/organisms/VideoPlayer"
import { MomentTitle } from "trux/dist/organisms/MomentTitle"
import * as links from "../../util/links"
import { SaveButton } from "../../components/SaveButton"

import { getPageViews } from "../../util/analytics"
import { eventsToMoments, eventToMoment } from "./eventsToMoments"
import { useHistory } from "react-router-dom"
import { useHotkeys } from "./useHotkeys"

export const GameVideo = ({
    containerRef,
    teamName,
    gameId,
    playlistId,
    eventNumber,
    fullEventNumber,
    cameraFilter,
    urlCamera,
    game,
    playlist,
    event,
    currentEvent,
    cameras,
    extras,
    forceActive,
    verticalVideoMode,
    videoPlayerDefaultCamera,
    showFluidPlayerOnboarding,
    setShowFluidPlayerOnboarding,
    showMulticamOnboarding,
    setShowMulticamOnboarding,
    showMulticamTooltip,
    setShowMulticamTooltip,
    showChurnedDialog,
    setGPVideoPlayerActive,
    setGPLoopCount,
    enableAddTags,
    onShareMoment,
    toast,
    openMomentsSelector,
    gotoPlayerPage,
    user,
}) => {
    const history = useHistory()
    const location = history.location
    const playlistEvents = useSelector((state) => {
        return state.video.value.playlistEvents
    })
    const color = isSmallMobileScreen() ? Color.Trace : Color.White2

    const [clipViews, setClipViews] = useState(-1)
    const [titleStyle, setTitleStyle] = useState({})

    const playlistMoments = useMemo(() => {
        return eventsToMoments(playlistEvents, game, playlist)
    }, [playlistEvents, game, playlist])

    // META Video Infos
    const { setLoop, setLocked } = useVideoSettings(containerRef)
    const [paused, setPaused] = useState(false)
    const [loopCount, setLoopCount] = useState(0)
    const [multicamOpen, setMulticamOpen] = useState(false)
    const [initialLoading, setInitialLoading] = useState(true)
    const [videoPlayerActive, setVideoPlayerActive] = useState(false)
    const {
        play,
        pause,
        currentMoment,
        goToNext,
        goToPrev,
        goToItem,
        setItemsAndSources,
        onItemChanged,
        handleVideoInfoUpdate,
        setTouches,
        insideMoment,
        playlistController,
    } = useFluidPlayer(containerRef, {
        paused: true,
        loopCount: true,
        multicamOpen: true,
        active: true,
        readyState: true,
        initialLoading: true,
    })

    // VIDEO: handle state updates
    useEffect(() => {
        handleVideoInfoUpdate((info) => {
            if (info.paused === false && info.currentTime > 0) {
                stopBenchmark("First Load")
            }
            if (info.paused !== paused) {
                setPaused(info.paused)
            }
            if (info.loopCount !== loopCount) {
                setLoopCount(info.loopCount)
                setGPLoopCount(info.loopCount)
            }
            if (info.multicamOpen !== multicamOpen) {
                setMulticamOpen(info.multicamOpen)
            }
            if (info.active !== videoPlayerActive) {
                setVideoPlayerActive(info.active)
                setGPVideoPlayerActive(info.active)
            }
            if (info.initialLoading !== initialLoading) {
                setInitialLoading(info.initialLoading)
            }
        })
    }, [
        paused,
        loopCount,
        multicamOpen,
        videoPlayerActive,
        handleVideoInfoUpdate,
    ])

    // VIDEO: update ui when we are inside or outside of a moment
    useEffect(() => {
        let style = ""
        let overflow = ""
        let opacity = ""
        if (insideMoment) {
            style = "62px"
            opacity = "1"
            overflow = "visible"
        } else {
            style = "0px"
            overflow = "hidden"
            opacity = "0"
        }
        setTitleStyle({ height: style, overflow: overflow, opacity: opacity })
    }, [insideMoment])

    // VIDEO: get playlist moment info for displaying timeline ticks
    const momentTicks = useMemo(() => {
        let ticks = []
        if (playlistMoments !== null) {
            for (let moment of playlistMoments) {
                ticks.push({
                    start_time: moment.start_time / 1000,
                    end_time:
                        moment.start_time / 1000 + moment.dynamic_duration,
                    tags: generateHumanTags(moment),
                    away: moment.away_team,
                    home: moment.home_team,
                    players: moment.players,
                })
            }
        }
        return ticks
    }, [playlistMoments])

    // VIDEO: update playlist moments and sources
    useEffect(() => {
        if (game && playlistMoments) {
            console.log(
                "GameVideo: updating playlist moments and sources",
                playlistMoments
            )
            let gameEvents = game.events
            let fullGameEvents = []

            let fullGameMoments = []
            let fullGameSources = []

            if (gameEvents.length > 0) {
                fullGameEvents = gameEvents.filter((m) => {
                    return m.meta.type === "FullGameVideo"
                })
            }

            let allCameraNames = []
            let firstTraceCamFullGame = null
            for (let fullevent of fullGameEvents) {
                let cn = getProperty("meta.camera.name", fullevent, "")
                if (cn === "tracecam") {
                    fullGameMoments.push(eventToMoment(fullevent, game))
                    if (firstTraceCamFullGame === null) {
                        firstTraceCamFullGame = fullevent
                    }
                } else {
                    if (!allCameraNames.includes(cn)) {
                        allCameraNames.push(cn)
                    }
                    fullGameSources.push(eventToMoment(fullevent, game))
                }
            }

            for (let fullMoment of fullGameMoments) {
                let cameraNames = [...allCameraNames]
                for (let fullSource of fullGameSources) {
                    let range = TimeRange.fromMoment(fullMoment)
                    let sourceTime = Time.fromMoment(fullSource)
                    if (range.contains(sourceTime)) {
                        let cn = getProperty("meta.camera.name", fullSource, "")
                        if (cameraNames.includes(cn)) {
                            cameraNames.splice(cameraNames.indexOf(cn), 1)
                        }
                        fullMoment.sources.push(fullSource)
                    }
                }
                for (let cn of cameraNames) {
                    let cameraMoment = fullGameSources.find((m) => {
                        let cn2 = getProperty("meta.camera.name", m, "")
                        if (cn2 === cn) {
                            return true
                        }
                    })
                    if (cameraMoment) {
                        fullMoment.sources.push(cameraMoment)
                    }
                }
            }

            let first = null
            for (let m of playlistMoments) {
                // eslint-disable-next-line eqeqeq
                if (m.event_number == eventNumber) {
                    first = m
                    break
                }
            }
            if (!first && playlistMoments.length > 0) {
                first = playlistMoments[0]
            }

            let gameTime = extras.gt
            if (firstTraceCamFullGame && !gameTime && extras.time) {
                // support old style time links
                let time = extras.time
                gameTime =
                    (firstTraceCamFullGame.utc_time - currentEvent.utc_time) /
                        1000 +
                    time
            }

            // if we dont have any video
            // there no need to try to get them to play
            if (first && playlistController) {
                setTouches(playlistMoments)
                setItemsAndSources(
                    fullGameMoments,
                    playlistMoments,
                    first.game_clip_id,
                    urlCamera,
                    gameTime
                )
                if (gameTime) {
                    // remove time
                    setTimeout(() => {
                        history.push(
                            history.location.pathname.replace(
                                `/time/${extras.time}`,
                                ""
                            )
                        )
                    }, 200)
                }
            }
        }
    }, [
        playlistController,
        game?.id,
        playlistMoments.map((m) => m.game_clip_id).join(","),
    ])

    // VIDEO: when event number changes tell the playlist controller
    useEffect(() => {
        // eslint-disable-next-line eqeqeq
        if (currentMoment && eventNumber != currentMoment.event_number) {
            // eslint-disable-next-line eqeqeq
            let m = playlistMoments.find((x) => x.event_number == eventNumber)
            if (m) {
                goToItem(m.game_clip_id)
            }
        }
    }, [eventNumber, currentMoment, goToItem])

    // VIDEO: update url when playlist item changes
    useEffect(() => {
        onItemChanged((moment) => {
            let urlParts = window.location.hash.split("/")
            let postFix = ""
            for (let i = 0; i < urlParts.length; i++) {
                if (i > 5) {
                    postFix += "/" + urlParts[i]
                }
            }
            let c =
                urlCamera === "all" || urlCamera === "tracecam"
                    ? ""
                    : `:${urlCamera}`
            history.push(
                `/${teamName}/games/${gameId}/${playlistId}/${moment.event_number}${c}${postFix}`
            )
        })
    }, [
        onItemChanged,
        history,
        teamName,
        gameId,
        playlistId,
        cameraFilter,
        urlCamera,
    ])

    // VIDEO: update locked and loop settings based on playlist
    useEffect(() => {
        if (playlistId === "all") {
            setLoop(false)
            setLocked(false)
        } else {
            setLoop(false)
            setLocked(true)
        }
    }, [playlistId])

    // close multicam tool tip when multicam opens
    useEffect(() => {
        if (showMulticamTooltip) {
            setShowMulticamTooltip(false)
        }
    }, [multicamOpen])

    // HOTKEYS: handle hotkeys
    useHotkeys({
        showMulticamOnboarding,
        showMulticamTooltip,
        showChurnedDialog,
        play,
        pause,
        toast,
        goToNext,
        goToPrev,
        paused,
        location,
    })

    // Clip Views
    // initialize or increment the loop count when event changes
    useEffect(() => {
        if (event) {
            if (clipViews >= 0) {
                // if moment views are loaded we can just increment the views
                setClipViews(clipViews + 1)
            } else {
                // Load current moment views, if not already loaded
                getPageViews(game.events.map((e) => e.game_clip_id))
                    .then((count) => {
                        setClipViews(count)
                    })
                    .catch((e) => {
                        console.warn(e)
                    })
            }
        }
    }, [event, loopCount])

    // handle randar evnets
    const onRadarEvent = (e) => {
        if (e.type === "tracer-click") {
            gotoPlayerPage(e)
        }
    }

    /*** variables needed for JSX  ***/
    let pageClassNames = ["GamePage"]
    if (verticalVideoMode) {
        pageClassNames.push("vertical")
        if (videoPlayerActive) {
            pageClassNames.push("video-active")
        }
    }

    let subtitle = "--"
    if (playlist) {
        if (playlistId === "full-game") {
            subtitle = event.title
        } else {
            subtitle = playlist.title
        }
    }
    /*** END: variables needed for JSX  ***/

    return (
        <>
            <VideoContainer
                forceActive={forceActive}
                className={verticalVideoMode ? "vertical" : "standard"}
                momentTicks={momentTicks}
                noMulticam={false}
                containerRef={containerRef}
                cameras={cameras}
                defaultCamera={videoPlayerDefaultCamera}
                onRadarEvent={onRadarEvent}
                toaster={toast}
                coachAudio={playlistId === "coach-notes"}
                trackingUserId={user?.user_id}
                trackingCategory="GO > Video > Play"
                trackingAction="Game Page"
                trackingPlayerType="Standard"
                fullScreenElementSelector={".App"}
                radar
                superFollow={false}
                watermark
                spotlight
                multicamTooltipOpen={showMulticamTooltip && !showChurnedDialog}
                onMulticamTooltipClose={() => setShowMulticamTooltip(false)}
                extraVideoControls={
                    <>
                        <VideoControlButton
                            trkAction="Share Pressed"
                            icon={Icon.Type.Action}
                            onClick={onShareMoment}
                            iconSize="24"
                            title="Share"
                        />
                        <div className="SaveButtonContainer mobile">
                            <SaveButton
                                game={game}
                                event={event}
                                history={history}
                                afterOnClick={null}
                                location={location}
                                insideMoment={insideMoment}
                                saveUrl={"save-moment/"}
                                createUrl={"create-moment"}
                                trkAction={"SaveButton Pressed"}
                            />
                        </div>
                    </>
                }
                extraUserControls={
                    <>
                        <VideoControlButton
                            trkAction="Players Pressed"
                            icon={Icon.Type.PlayersGeneric}
                            iconColor={color}
                            onClick={(e) =>
                                history.push(
                                    links.gamePlayersUrl(
                                        teamName,
                                        playlistId,
                                        gameId,
                                        fullEventNumber
                                    )
                                )
                            }
                            iconSize="24"
                            hideSM
                            title="Players"
                        />
                        <VideoControlButton
                            trkAction="More Pressed"
                            icon={Icon.Type.More}
                            iconColor={color}
                            onClick={(e) =>
                                history.push(
                                    links.gameOptionsUrl(
                                        teamName,
                                        playlistId,
                                        gameId,
                                        fullEventNumber
                                    )
                                )
                            }
                            iconSize="24"
                            title="Highlight options"
                        />
                        <div className="SaveButtonContainer desktop">
                            <SaveButton
                                game={game}
                                event={event}
                                history={history}
                                afterOnClick={null}
                                location={location}
                                insideMoment={insideMoment}
                                saveUrl={"save-moment/"}
                                createUrl={"create-moment"}
                                trkAction={"SaveButton Pressed"}
                            />
                        </div>
                    </>
                }
            >
                <Row className="clickable-controls">
                    <MomentTitle
                        titleStyle={titleStyle}
                        playlist={subtitle}
                        moment={currentEvent}
                        momentCount={playlistEvents.length}
                        highlightPlayer={getProperty(
                            "scope.player",
                            playlist,
                            null
                        )}
                        tags={getProperty("tags", currentEvent, [])}
                        players={getProperty("players", event, [])}
                        onAvatarClick={gotoPlayerPage}
                        homeColor={getProperty(
                            "home_team.jersey_color",
                            game,
                            "#000000"
                        )}
                        awayColor={getProperty(
                            "away_team.jersey_color",
                            game,
                            "#ffffff"
                        )}
                        clipViews={clipViews > 0 ? clipViews : 1}
                        showAddTagsButton={enableAddTags}
                        onCheveronClick={openMomentsSelector}
                        avatarSize={isSmallScreen() ? 32 : 40}
                        insideMoment={insideMoment}
                    />
                    <Element className="clickable-area" />
                </Row>
            </VideoContainer>
            {showMulticamOnboarding &&
                !showChurnedDialog &&
                !initialLoading && (
                    <MulticamOnboardingDialog
                        videoContainerRef={containerRef}
                        open={showMulticamOnboarding}
                        onClose={(e) => setShowMulticamOnboarding(false)}
                    />
                )}
            {showFluidPlayerOnboarding &&
                !showChurnedDialog &&
                !initialLoading && (
                    <FluidPlayerOnboardingDialog
                        videoContainerRef={containerRef}
                        open={showFluidPlayerOnboarding}
                        onClose={(e) => setShowFluidPlayerOnboarding(false)}
                    />
                )}
        </>
    )
}
